<template>
  <div class="tw-rounded-sm tw-bg-white tw-h-full tw-p-6">
    <div class="tw-flex tw-justify-between tw-items-center">
      <div class="tw-flex tw-items-center">
        <div
          v-tippy="{
            content: $t('email-security-solution'),
            placement: 'top',
          }"
          class="tw-mr-2 tw-flex tw-h-6 tw-w-6 tw-items-center tw-justify-center tw-place-self-center tw-rounded-full tw-bg-white tw-p-0.5"
          :data-test-id="`${tier}-emails-chart-icon`"
        >
          <component :is="SERVICE_ICON.PHISH_SERVICE" />
        </div>

        <div class="tw-text-lg tw-font-semibold">
          {{ $t("emails-analyzed-vs-incidents") }}
        </div>
      </div>
      <div
        v-tippy="{
          content: $t('email-analyzed-vs-incidents'),
          placement: 'top',
        }"
        class="tw-text-primary-grey-200"
        :data-test-id="`${tier}-emails-chart-info-icon`"
      >
        <cb-icon-information :width="18" />
      </div>
    </div>
    <div class="tw-h-80 tw-w-full">
      <div
        v-if="isLoading"
        class="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full tw-animate-pulse tw-rounded-sm tw-bg-primary-grey-50/40"
      ></div>
      <empty-chart-placeholder v-if="isDataEmpty || isError">
        <template #image>
          <cl-icon-empty-ufo v-if="isError" :width="'216'" :height="'162'" />
          <cl-icon-empty-graph v-else :width="'216'" :height="'162'" />
        </template>
        <template #title>
          <p>{{ placeholderTitle }}</p>
        </template>
        <template #description>
          <p>{{ placeholderMessage }}</p>
        </template>
      </empty-chart-placeholder>
      <template v-else>
        <div class="tw-h-80">
          <div :data-test-id="`${tier}-emails-chart`" class="tw-h-[90%]">
            <Line
              :data="chartDataset"
              :options="chartOptions"
              height="100%"
              width="100%"
            />
          </div>
          <div class="tw-flex tw-justify-center">
            <div
              class="tw-flex tw-items-center tw-mx-2"
              @click="toggleIsSatCompletedHidden()"
            >
              <span
                :class="[
                  'tw-flex tw-items-center tw-bg-primary-grey-50 hover:tw-bg-primary-grey-100 tw-p-2 tw-cursor-pointer tw-rounded-md tw-text-sm',
                  { 'tw-line-through': isEmailAnalysedHidden },
                ]"
                role="button"
              >
                <span
                  class="tw-w-4 tw-h-4 tw-bg-[#0e9cff] tw-inline-block tw-rounded-full tw-mr-2"
                ></span>
                {{ t("emails-analyzed-legend") }}
              </span>
            </div>
            <div
              class="tw-flex tw-items-center tw-mx-2"
              @click="toggleIsClickedHidden()"
            >
              <span
                :class="[
                  'tw-flex tw-items-center tw-bg-primary-grey-50 hover:tw-bg-primary-grey-100 tw-p-2 tw-cursor-pointer tw-rounded-md tw-text-sm',
                  { 'tw-line-through': isIncidentsHidden },
                ]"
                role="button"
              >
                <span
                  class="tw-w-4 tw-h-4 tw-bg-[#d23f3f] tw-inline-block tw-rounded-full tw-mr-2"
                ></span>
                {{ t("incidents-legend") }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { Line } from "vue-chartjs";
import {
  Chart,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
} from "chart.js";
import { useIsMspRoute } from "@/composables/useIsMspRoute";
import { useGlobalOrganisationIDBasedOnRoute } from "@/composables/useGlobalOrganisationIDBasedOnRoute";
import { CUSTOMER, MSP } from "@/constants";
import { SERVICE_ICON } from "@/utils/services";
import { blue, gradientBlue, gradientRed, red } from "./colors";
import EmptyChartPlaceholder from "@/components/EmptyChartPlaceholder/EmptyChartPlaceholder.vue";
import { useToggle } from "@vueuse/core";
import { useEmailsChartStore } from "./useEmailsChart.store";
import { useMessagesAnalysedStore } from "./useMessagesAnalysed.store";

const props = defineProps<{ startDate: string }>();
Chart.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Filler,
);

const { isMspRoute } = useIsMspRoute();
const { globalOrganisationId } = useGlobalOrganisationIDBasedOnRoute();

const isLoading = ref(true);
const isError = ref(false);
const organisationType = computed(() => (isMspRoute.value ? MSP : CUSTOMER));
const [isEmailAnalysedHidden, toggleIsSatCompletedHidden] = useToggle(false);
const [isIncidentsHidden, toggleIsClickedHidden] = useToggle(false);

const style = getComputedStyle(document.body);
const textColor = style.getPropertyValue("--chartWhite");

const { t } = useI18n();
const { resolutionHistory, getResolutionHistory } = useEmailsChartStore();
const messagesAnalysedStore = useMessagesAnalysedStore();
const { messagesAnalysed } = storeToRefs(messagesAnalysedStore);

const incidents = computed<{ period: string; total: number }[]>(
  () => resolutionHistory.value,
);
const tier = isMspRoute.value ? "msp" : "customer";

const emailsAnalysed = computed<{ period: string; total: number }[]>(
  () => messagesAnalysed.value,
);

const chartLabels = computed(() =>
  incidents.value.map((item: { period: string }) => {
    const date = new Date(item.period);
    const formatter = new Intl.DateTimeFormat("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });

    return formatter.format(date);
  }),
);

const isDataEmpty = computed<boolean>(() => {
  const emailsEmpty = emailsAnalysed.value.length === 0;
  const incidentsEmpty = incidents.value.length === 0;
  const allSentZero =
    emailsAnalysed.value.every((val) => val.total === 0) &&
    incidents.value.every((val) => val.total === 0);

  return (emailsEmpty && incidentsEmpty) || allSentZero;
});

const chartDataset = computed(() => {
  return {
    labels: chartLabels.value,
    datasets: [
      {
        label: t("emails-analyzed-legend"),
        data: emailsAnalysed.value.map(
          (email: { total: number }) => email.total,
        ),
        backgroundColor: gradientBlue,
        pointBackgroundColor: blue.default,
        borderColor: blue.default,
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.4,
        fill: true,
        type: "line" as const,
        yAxisID: "y",
        hidden: isEmailAnalysedHidden.value,
      },
      {
        label: t("incidents-legend"),
        data: incidents.value.map(
          (incident: { total: number }) => incident.total,
        ),
        backgroundColor: gradientRed,
        pointBackgroundColor: red.default,
        borderColor: red.default,
        borderWidth: 2,
        pointRadius: 3,
        tension: 0.4,
        fill: true,
        type: "line" as const,
        yAxisID: "y1",
        hidden: isIncidentsHidden.value,
      },
    ],
  };
});
const chartOptions = computed(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    scales: {
      x: {
        grid: {
          display: false,
          drawTicks: false,
        },
        border: {
          display: false,
        },
        ticks: {
          padding: 10,
          color: textColor,
          font: {
            size: 14,
          },
        },
        offset: false,
      },
      y: {
        position: "left" as const,
        beginAtZero: true,
        title: {
          display: true,
          text: t("emails-analyzed-legend"),
          color: blue.default,
          font: {
            size: 16,
          },
        },
        border: {
          display: false,
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        ticks: {
          padding: 10,
          precision: 0,
          color: blue.default,
          font: {
            size: 16,
          },
        },
      },
      y1: {
        position: "right" as const,
        beginAtZero: true,
        title: {
          display: true,
          text: t("incidents-legend"),
          color: red.default,
          font: {
            size: 16,
          },
        },
        border: {
          display: false,
        },
        grid: {
          drawOnChartArea: false,
          drawTicks: false,
        },
        ticks: {
          display: true,
          padding: 10,
          precision: 0,
          color: red.default,
          font: {
            size: 16,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };
});

const getResolutionAndMessages = async () => {
  if (!globalOrganisationId.value) return;

  try {
    isError.value = false;
    isLoading.value = true;

    await Promise.allSettled([
      getResolutionHistory({
        globalOrganisationID: globalOrganisationId.value,
        params: {
          interval: "daily",
          start_date: props.startDate,
          organisationType: organisationType.value,
        },
      }),
      messagesAnalysedStore.getMessagesAnalysed({
        globalOrganisationID: globalOrganisationId.value,
        params: {
          interval: "daily",
          start_date: props.startDate,
          organisationType: organisationType.value,
        },
      }),
    ]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    isError.value = true;
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.startDate,
  () => {
    getResolutionAndMessages();
  },
);

onMounted(async () => {
  await getResolutionAndMessages();
});

const placeholderTitle = computed<string>(() => {
  return isError.value ? t("sys-error-default") : t("no-data");
});

const placeholderMessage = computed<string>(() => {
  return isError.value
    ? t("subtext-sys-error-default")
    : t("subtext-emails-analyzed-incidents");
});
</script>
